/* Upload / replace document dialog (split from CDMDocumentsSettings.jsx) */
import React from 'react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  AlertCircle, AlertTriangle, CheckCircle, FileText, Loader2, RefreshCw,
  Upload, X, XCircle,
} from 'lucide-react';
import { ScanOptionsPanel } from '../../../components/ScanOptionsPanel';
import { API_URL, authedFetch } from './api';
import { SUPPORTED_EXTENSIONS } from './constants';

export function UploadDocumentDialog(props) {
  const {
  collections, dragOver, handleFileSelect, handleUpload, newCollectionName,
  parentDocsForReplace, removeUploadFile, replaceMode, replaceTargetDoc, resetScanOptsForDialog,
  scanOptsUpload, setDragOver, setNewCollectionName, setParentDocsForReplace, setReplaceMode,
  setReplaceTargetDoc, setScanOptsUpload, setShowNewCollection, setShowUploadDialog, setUploadCancelled,
  setUploadCollectionId, showNewCollection, showUploadDialog, systemCapacity, updateScanOpts,
  uploadCancelledRef, uploadCollectionId, uploadError, uploadFiles, uploadProgress,
  uploading,
  } = props;

  return (<>
      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => { if (!open && !uploading) setShowUploadDialog(false); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload Documents to RAG V4</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2 overflow-y-auto flex-1 min-h-0">
            {/* Level 6: Capacity warning in upload dialog */}
            {systemCapacity && !systemCapacity.accept && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800" data-testid="upload-capacity-blocked">
                <XCircle className="h-4 w-4 shrink-0 text-red-500" />
                <span>{systemCapacity.reason || 'System is at capacity. Please wait before uploading.'}</span>
              </div>
            )}
            {systemCapacity && systemCapacity.accept && systemCapacity.level === 'warning' && (
              <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-testid="upload-capacity-warning">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                <span>{systemCapacity.reason || 'System is busy. Uploads will be queued.'}</span>
              </div>
            )}
            {/* Phase 11.5 Issue H: Replace existing document option */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="replace-mode-checkbox"
                checked={replaceMode}
                onChange={(e) => {
                  setReplaceMode(e.target.checked);
                  if (e.target.checked && parentDocsForReplace.length === 0) {
                    // Fetch parent file-upload documents for the dropdown
                    authedFetch(`${API_URL}/api/rag/cdm/documents?parent_filter=parents_only&page=1&page_size=200`)
                      .then(r => r.json())
                      .then(d => setParentDocsForReplace((d.documents || []).filter(doc =>
                        !['url', 'article'].includes(doc.doc_type) && doc.source_type !== 'zoho_kb_article' && doc.source_type !== 'url'
                      )))
                      .catch(() => {});
                  }
                  if (!e.target.checked) setReplaceTargetDoc(null);
                }}
                className="rounded"
                data-testid="replace-mode-checkbox"
              />
              <Label htmlFor="replace-mode-checkbox" className="text-xs">Replace existing document</Label>
            </div>
            {replaceMode && (
              <div className="space-y-2">
                <Select value={replaceTargetDoc?.doc_id || ''} onValueChange={(val) => {
                  const doc = parentDocsForReplace.find(d => d.doc_id === val);
                  setReplaceTargetDoc(doc || null);
                }}>
                  <SelectTrigger className="text-xs h-8" data-testid="replace-target-select">
                    <SelectValue placeholder="Select document to replace..." />
                  </SelectTrigger>
                  <SelectContent>
                    {parentDocsForReplace.map(d => (
                      <SelectItem key={d.doc_id} value={d.doc_id} className="text-xs">
                        {d.title?.substring(0, 60) || d.source_uri?.substring(0, 60) || d.doc_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {replaceTargetDoc && (
                  <p className="text-[10px] text-gray-500">Will replace: {replaceTargetDoc.title} (version: {replaceTargetDoc.version_id || 'v1'})</p>
                )}
              </div>
            )}

            {/* Drag & Drop / File Picker */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                dragOver ? 'border-blue-500 bg-blue-50' :
                uploadFiles.length > 0 ? 'border-green-400 bg-green-50' :
                'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
              }`}
              data-testid="upload-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (!uploading) handleFileSelect(e.dataTransfer.files);
              }}
              onClick={() => { if (!uploading) document.getElementById('cdm-file-input')?.click(); }}
            >
              <input
                id="cdm-file-input"
                type="file"
                className="hidden"
                multiple
                accept={SUPPORTED_EXTENSIONS.join(',')}
                onChange={(e) => {
                  if (e.target.files?.length) handleFileSelect(e.target.files);
                  e.target.value = '';
                }}
                data-testid="upload-file-input"
              />
              <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">
                Drag & drop files here, or <span className="text-blue-600 font-medium">click to browse</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                PDF, DOCX, XLSX, PPTX, CSV, TXT, Images, Audio (MP3/WAV/OGG), Video (MP4/AVI/MOV)
              </p>
            </div>

            {/* File List */}
            {uploadFiles.length > 0 && (
              <div className="border rounded-lg divide-y max-h-48 overflow-y-auto" data-testid="upload-file-list">
                {uploadFiles.map((entry, idx) => (
                  <div key={idx} className={`flex items-center justify-between px-3 py-2 text-sm ${
                    entry.status === 'invalid' ? 'bg-red-50' :
                    entry.status === 'success' ? 'bg-green-50' :
                    entry.status === 'failed' ? 'bg-red-50' : ''
                  }`}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <FileText className={`h-4 w-4 flex-shrink-0 ${
                        entry.status === 'invalid' || entry.status === 'failed' ? 'text-red-500' :
                        entry.status === 'success' ? 'text-green-500' : 'text-gray-400'
                      }`} />
                      <span className="truncate">{entry.file.name}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">{(entry.file.size / 1024 / 1024).toFixed(1)}MB</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {entry.status === 'success' && <CheckCircle className="h-4 w-4 text-green-500" />}
                      {entry.status === 'failed' && <span className="text-xs text-red-600 max-w-32 truncate" title={entry.error}>{entry.error}</span>}
                      {entry.status === 'invalid' && <span className="text-xs text-red-600 max-w-32 truncate" title={entry.error}>{entry.error}</span>}
                      {!uploading && entry.status === 'pending' && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); removeUploadFile(idx); }} className="p-0.5 hover:bg-gray-200 rounded">
                          <X className="h-3 w-3 text-gray-500" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* File count and size summary */}
            {uploadFiles.length > 0 && (
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{uploadFiles.filter(f => f.status === 'pending').length} files ready, {uploadFiles.filter(f => f.status === 'invalid').length > 0 ? `${uploadFiles.filter(f => f.status === 'invalid').length} invalid` : ''}</span>
                <span>Total: {(uploadFiles.reduce((s, f) => s + f.file.size, 0) / 1024 / 1024).toFixed(1)}MB</span>
              </div>
            )}

            {/* Scan Options for File Upload */}
            {uploadFiles.length > 0 && (
              <>
                <ScanOptionsPanel
                  value={scanOptsUpload}
                  onChange={(v) => updateScanOpts('upload', setScanOptsUpload, v)}
                  onResetToDefaults={() => resetScanOptsForDialog('upload', setScanOptsUpload)}
                  collapsible={true}
                  defaultExpanded={false}
                />
                <p className="text-[10px] text-gray-400 px-1" data-testid="upload-enrichment-note">
                  Image descriptions are extracted automatically. Hyperlinks inside the document will be followed based on your Scan Options.
                </p>
              </>
            )}

            {/* Upload Progress */}
            {uploading && uploadProgress.total > 0 && (
              <div className="space-y-2" data-testid="upload-progress">
                <div className="flex items-center justify-between text-sm">
                  <span>Uploading {uploadProgress.current} of {uploadProgress.total}...</span>
                  <span className="text-xs text-gray-500">{uploadProgress.succeeded} done, {uploadProgress.failed} failed</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Error */}
            {uploadError && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700" data-testid="upload-error">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p>{uploadError}</p>
              </div>
            )}

            {/* Collection */}
            <div>
              <label className="text-sm font-medium text-gray-700">Collection</label>
              {!showNewCollection ? (
                <div className="mt-1">
                  <Select
                    value={uploadCollectionId || 'General'}
                    onValueChange={(val) => {
                      if (val === '__new__') {
                        setShowNewCollection(true);
                        setNewCollectionName('');
                      } else {
                        setUploadCollectionId(val);
                      }
                    }}
                    disabled={uploading}
                  >
                    <SelectTrigger className="w-full" data-testid="upload-collection-dropdown">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {collections.map(c => (
                        <SelectItem key={c} value={c}>
                          <span className="flex items-center gap-2">
                            {c}
                            {c === 'General' && <span className="text-xs text-gray-400">(Default)</span>}
                          </span>
                        </SelectItem>
                      ))}
                      <SelectItem value="__new__">
                        <span className="text-blue-600 font-medium">+ Create New Collection</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="mt-1 space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={newCollectionName}
                      onChange={(e) => setNewCollectionName(e.target.value)}
                      placeholder="Enter new collection name"
                      className="flex-1"
                      data-testid="new-collection-input"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const name = newCollectionName.trim();
                          if (name) { setUploadCollectionId(name); setShowNewCollection(false); }
                        }
                      }}
                    />
                    <Button type="button" size="sm" onClick={() => { const name = newCollectionName.trim(); if (name) { setUploadCollectionId(name); setShowNewCollection(false); } }} disabled={!newCollectionName.trim()}>Add</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => { setShowNewCollection(false); setUploadCollectionId('General'); }}>Cancel</Button>
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">
                All files will be added to "{uploadCollectionId || 'General'}".
              </p>
            </div>

            {/* Buttons — pinned at bottom, outside scroll area */}
            <div className="flex justify-end gap-2 pt-3 border-t mt-2 flex-shrink-0">
              {uploading ? (
                <Button variant="outline" onClick={() => { setUploadCancelled(true); uploadCancelledRef.current = true; }} data-testid="upload-cancel-btn">
                  Cancel Upload
                </Button>
              ) : (
                <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
                  Close
                </Button>
              )}
              <Button
                onClick={handleUpload}
                disabled={uploadFiles.filter(f => f.status === 'pending').length === 0 || uploading || (replaceMode && !replaceTargetDoc)}
                data-testid="upload-submit-btn"
              >
                {uploading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
                ) : replaceMode ? (
                  <><RefreshCw className="h-4 w-4 mr-2" />Replace &amp; Re-ingest</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" />
                    {uploadFiles.filter(f => f.status === 'pending').length > 1
                      ? `Upload ${uploadFiles.filter(f => f.status === 'pending').length} Files`
                      : 'Upload & Ingest'
                    }
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
  </>);
}
